import React, { useEffect, useState } from "react";
import { Input, Divider, Typography, Spin } from "antd";
import CustomButton from "../../../../common/custom-button/custom-button";
import CustomTable from "../../../../common/custom-table/custom-table";
import styles from "./examination.module.css";
import { getReportCardExaminationList } from "../../../../services/reportCard";

const ExaminationList = ({ onViewDetails, selectedYear }) => {
  const [searchValue, setSearchValue] = useState("");
  const [examinationList, setExaminationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchExaminationList = async (tempPage = pageNo, tempPageSize = pageSize) => {
    setLoading(true);
    try {
      const params = {
        page: tempPage,
        pageSize: tempPageSize,
        status: "Completed",
        academic_year_id: selectedYear,
      };

      if (searchValue) {
        params.name = searchValue;
      }

      const response = await getReportCardExaminationList(params);
      const apiData = response?.data?.data || [];

      const mappedData = apiData.map((exam, index) => ({
        key: exam.id || index,
        id: exam.id,
        name: exam.exam_name,
        type: exam.examination_type,
        start: exam.exam_start_date,
        end: exam.exam_end_date,
        status: exam.status,
        raw: exam,
      }));

      setExaminationList(mappedData);
      setTotalCount(response?.data?.totalRecords);
    } catch (error) {
      console.error("Error fetching examination list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeOut = setTimeout(() => {
      if (selectedYear) {
        setPageNo(1);
        fetchExaminationList();
      }
    }, 500);
    return () => {
      clearTimeout(timeOut);
    };
  }, [searchValue, selectedYear]);

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    fetchExaminationList(page, pageSize);
  };

  const columns = [
    {
      title: "Examination Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Examination Type",
      dataIndex: "type",
      key: "type",
      sorter: (a, b) => a.type.localeCompare(b.type),
    },
    {
      title: "Exam Start Date",
      dataIndex: "start",
      key: "start",
      sorter: (a, b) => new Date(a.start) - new Date(b.start),
    },
    {
      title: "Exam End Date",
      dataIndex: "end",
      key: "end",
      sorter: (a, b) => new Date(a.end) - new Date(b.end),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status) => {
        const colorMap = {
          Completed: "#3BB44B",
          Ongoing: "#1E9EFD",
          Upcoming: "#F0AF00",
        };

        const bgColorMap = {
          Completed: "#ECF8ED",
          Ongoing: "#F0F8FF",
          Upcoming: "#FFFEE4",
        };

        return (
          <span
            className={styles.statusPill}
            style={{
              backgroundColor: bgColorMap[status],
              color: colorMap[status],
            }}
          >
            <span
              className={styles.statusDot}
              style={{ backgroundColor: colorMap[status] }}
            />
            <span>{status}</span>
          </span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <CustomButton
          title="View Details"
          onClick={() => onViewDetails(record)}
        />
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div>
        <div className={styles.header}>
          <Typography.Title level={5} className={styles.title}>
            Examination List
          </Typography.Title>
          <Input.Search
            placeholder="Search by keyword"
            onChange={handleSearch}
            className={styles.searchInput}
          />
        </div>

        <Divider className={styles.divider} />

        <CustomTable
          columns={columns}
          dataSource={examinationList}
          total={totalCount}
          pageSize={pageSize}
          currentPage={pageNo}
          handlePageChange={handleChangePage}
        />
      </div>
    </Spin>
  );
};

export default ExaminationList;
